import * as React from 'react';
import { format, isToday, isTomorrow } from 'date-fns';
import { Video } from 'lucide-react';
import { cn } from '../../utils';
import { Card, CardContent, CardHeader, CardTitle } from '../core/card';
import { Skeleton } from '../core/skeleton';
import { ParticipantStack, type ParticipantStackHost } from './participant-stack';
import { getJoinState } from './event-utils';

const ONE_HOUR_MS = 60 * 60 * 1000;

export type AgendaBadgeKind = 'group' | 'personal' | 'event';

export interface AgendaItem {
  id: string;
  title: string;
  /** ISO datetime */
  startTime: string;
  /** ISO datetime */
  endTime: string | null;
  badgeKind: AgendaBadgeKind;
  host: ParticipantStackHost;
  /** People invited to the event, excluding the host. */
  othersCount: number;
  isLive: boolean;
  /**
   * Meeting URL. When present, the built-in camera join button renders on the
   * row from 1 hour before start until the event ends (pulse = soon,
   * ping = live). No URL or outside the window → no camera.
   */
  joinHref?: string | null;
  /** Plain URL the row links to. Rows without one render unwrapped. */
  href?: string | null;
}

export interface EventAgendaLabels {
  title: string;
  empty: string;
  seeAll?: string;
  today: string;
  tomorrow: string;
  /** e.g. "Starts in {count} min" — receives the minutes until start. */
  startsInMinutes: (minutes: number) => string;
  /** e.g. "Starts at {time}" — receives the formatted time. */
  startsAt: (time: string) => string;
  kind: Record<AgendaBadgeKind, string>;
  /** Accessible label for the built-in camera join button. Defaults to "Join". */
  join?: string;
}

export interface EventAgendaProps {
  items: AgendaItem[];
  isLoading?: boolean;
  labels: EventAgendaLabels;
  seeAllHref?: string;
  /**
   * Opens the in-app detail for a row. When provided, every row becomes a
   * button (unless `renderItemWrapper` claims it first).
   */
  onSelectItem?: (item: AgendaItem) => void;
  /**
   * Router-aware wrapper for rows that navigate internally. When it returns
   * null/undefined for an item, the row falls back to `onSelectItem`, then
   * `item.href` (plain <a>), then renders unwrapped.
   */
  renderItemWrapper?: (item: AgendaItem, children: React.ReactNode) => React.ReactNode | null;
  /**
   * Trailing slot per row. Overrides the built-in camera join button — most
   * consumers should omit this and set `joinHref` on their items instead.
   */
  renderItemAction?: (item: AgendaItem) => React.ReactNode;
  className?: string;
}

const SOON_WINDOW_MIN = 60;

/** Maps an Event.eventKind to the agenda badge bucket. */
export function badgeKindForEventKind(eventKind: string | null | undefined): AgendaBadgeKind {
  switch (eventKind) {
    case 'group-class':
      return 'group';
    case 'private-class':
    case 'tutoring':
      return 'personal';
    default:
      return 'event';
  }
}

const badgeDotClass: Record<AgendaBadgeKind, string> = {
  group: 'bg-[#cf2e2e]',
  personal: 'bg-amber-500',
  event: 'bg-violet-500',
};

/** "Solid Red" treatment applies to rows happening today that have not ended. */
function isHotItem(item: AgendaItem): boolean {
  const start = new Date(item.startTime);
  const startMs = start.getTime();
  if (!Number.isFinite(startMs)) return false;
  const endMs = item.endTime ? new Date(item.endTime).getTime() : startMs + ONE_HOUR_MS;
  return isToday(start) && endMs >= Date.now();
}

/**
 * Built-in camera join button (icon-only, round). Ping animation when live,
 * pulse when starting soon; both disabled under prefers-reduced-motion.
 * On solid red rows it switches to the on-red (white) treatment.
 */
function AgendaJoinCamera({
  href,
  state,
  onRed,
  label,
}: {
  href: string;
  state: 'soon' | 'live';
  onRed: boolean;
  label: string;
}) {
  const surface = onRed
    ? 'border border-white/40 bg-white/[0.22] text-white hover:bg-white/30 focus-visible:ring-white/60'
    : state === 'live'
      ? 'bg-[#cf2e2e] text-white hover:bg-[#b82828] focus-visible:ring-rose-300'
      : 'border-[1.5px] border-[#cf2e2e]/50 bg-white text-[#cf2e2e] hover:bg-rose-50 focus-visible:ring-rose-300';
  const halo = onRed ? 'bg-white/35' : state === 'live' ? 'bg-[#cf2e2e]/45' : 'bg-[#cf2e2e]/20';
  const animation =
    state === 'live'
      ? 'animate-[ping_1.2s_ease-out_infinite]'
      : 'animate-[pulse_1.3s_ease-in-out_infinite]';

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition focus:outline-none focus-visible:ring-2',
        surface,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 rounded-full motion-reduce:animate-none',
          halo,
          animation,
        )}
      />
      <Video className="relative h-4 w-4" />
    </a>
  );
}

/**
 * Presentational agenda list: chronological, grouped by day, one participant
 * stack per row. Consumers fetch and scope their own data (student: only their
 * events; teacher: everything they're linked to) and map it to AgendaItem[].
 *
 * Built-in defaults (design "Variant B — Solid Red"): rows happening today
 * that haven't ended render as filled red cards, the "Today" group label turns
 * red, and rows with a `joinHref` get a camera button inside the join window.
 */
export function EventAgenda({
  items,
  isLoading,
  labels,
  seeAllHref,
  onSelectItem,
  renderItemWrapper,
  renderItemAction,
  className,
}: EventAgendaProps) {
  const groups = React.useMemo(() => {
    // Dedupe defensively: drop repeated ids (upstream joins can double rows)
    // and collapse distinct rows that would render identically (same trimmed
    // title + same start/end minute) — happens e.g. when the same session is
    // materialized once per linked group and the viewer belongs to several.
    const seenIds = new Set<string>();
    const seenSignatures = new Set<string>();
    const minuteKey = (iso: string | null | undefined) => {
      if (!iso) return '';
      const ms = Date.parse(iso);
      return Number.isFinite(ms) ? Math.floor(ms / 60_000) : iso;
    };
    const deduped = items.filter((item) => {
      const signature = `${item.title.trim()}|${minuteKey(item.startTime)}|${minuteKey(item.endTime)}`;
      if (seenIds.has(item.id) || seenSignatures.has(signature)) return false;
      seenIds.add(item.id);
      seenSignatures.add(signature);
      return true;
    });

    const map = new Map<string, { key: string; label: string; isToday: boolean; items: AgendaItem[] }>();
    for (const item of deduped) {
      const date = new Date(item.startTime);
      const key = date.toDateString();
      const today = isToday(date);
      const label = today
        ? labels.today
        : isTomorrow(date)
          ? labels.tomorrow
          : format(date, 'EEEE, MMM d');
      if (!map.has(key)) map.set(key, { key, label, isToday: today, items: [] });
      map.get(key)!.items.push(item);
    }
    return Array.from(map.values());
  }, [items, labels.today, labels.tomorrow]);

  return (
    <Card className={cn('flex flex-col rounded-2xl', className)}>
      <CardHeader className="flex shrink-0 flex-row items-center justify-between gap-2 pb-3">
        <CardTitle as="h3" className="text-base font-semibold">
          {labels.title}
        </CardTitle>
        {seeAllHref && labels.seeAll && (
          <a href={seeAllHref} className="text-xs font-medium text-brand hover:underline">
            {labels.seeAll}
          </a>
        )}
      </CardHeader>
      {/* min-h-0 lets the list scroll when the consumer caps the card's height */}
      <CardContent className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <EventAgendaSkeleton />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{labels.empty}</p>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.key} className="space-y-2">
                <p
                  className={cn(
                    'text-[11px] font-semibold uppercase tracking-[0.2em]',
                    group.isToday ? 'text-[#cf2e2e]' : 'text-gray-400',
                  )}
                >
                  {group.label}
                </p>
                <div className="space-y-2">
                  {group.items.map((item) => (
                    <AgendaRow
                      key={item.id}
                      item={item}
                      labels={labels}
                      onSelectItem={onSelectItem}
                      renderItemWrapper={renderItemWrapper}
                      renderItemAction={renderItemAction}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AgendaRow({
  item,
  labels,
  onSelectItem,
  renderItemWrapper,
  renderItemAction,
}: {
  item: AgendaItem;
  labels: EventAgendaLabels;
  onSelectItem?: EventAgendaProps['onSelectItem'];
  renderItemWrapper?: EventAgendaProps['renderItemWrapper'];
  renderItemAction?: EventAgendaProps['renderItemAction'];
}) {
  const hot = isHotItem(item);

  const timeLine = (() => {
    const start = new Date(item.startTime);
    const nowMs = Date.now();
    const minsUntil = Math.round((start.getTime() - nowMs) / 60_000);
    if (isToday(start) && minsUntil > 0 && minsUntil <= SOON_WINDOW_MIN) {
      return labels.startsInMinutes(minsUntil);
    }
    if (isToday(start)) {
      return labels.startsAt(format(start, 'h:mm a'));
    }
    const end = item.endTime ? new Date(item.endTime) : null;
    return end ? `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}` : format(start, 'h:mm a');
  })();

  // Consumer-provided trailing slot wins; otherwise the built-in camera
  // renders whenever the item has a meeting URL inside the join window.
  const joinState = item.joinHref ? getJoinState(item.startTime, item.endTime) : null;
  const action = renderItemAction ? (
    renderItemAction(item)
  ) : item.joinHref && joinState ? (
    <AgendaJoinCamera
      href={item.joinHref}
      state={joinState}
      onRed={hot}
      label={labels.join ?? 'Join'}
    />
  ) : null;

  const inner = (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border p-3 transition',
        hot
          ? 'border-[#b82222] bg-gradient-to-br from-[#d63434] to-[#b82222] shadow-[0_8px_20px_rgba(207,46,46,0.28)]'
          : 'border-rose-50 bg-white/70 hover:border-rose-100 hover:bg-white',
      )}
    >
      <ParticipantStack
        host={item.host}
        othersCount={item.othersCount}
        tone={hot ? 'onRed' : 'default'}
      />
      <div className="min-w-0 flex-1">
        <p className={cn('truncate text-sm font-semibold', hot ? 'text-white' : 'text-gray-900')}>
          {item.title}
        </p>
        <p className={cn('mt-0.5 text-xs', hot ? 'font-semibold text-white/85' : 'text-gray-500')}>
          {timeLine}
        </p>
        <div className="mt-1.5 flex items-center gap-1.5">
          <span
            className={cn(
              'inline-block h-1.5 w-1.5 rounded-full',
              hot ? 'bg-white' : badgeDotClass[item.badgeKind],
            )}
          />
          <span
            className={cn(
              'text-[11px] font-medium uppercase tracking-wide',
              hot ? 'text-white/70' : 'text-gray-400',
            )}
          >
            {labels.kind[item.badgeKind]}
          </span>
        </div>
      </div>
      {action ? (
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          {action}
        </div>
      ) : null}
    </div>
  );

  const wrapped = renderItemWrapper?.(item, inner);
  if (wrapped) return <>{wrapped}</>;

  if (onSelectItem) {
    return (
      <button
        type="button"
        onClick={() => onSelectItem(item)}
        className="block w-full rounded-xl text-left outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
      >
        {inner}
      </button>
    );
  }

  if (item.href) {
    return (
      <a
        href={item.href}
        className="block rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
      >
        {inner}
      </a>
    );
  }

  return inner;
}

function EventAgendaSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
